import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface ReorderItem {
  product_id: string;
  product_name: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  location_id: string;
  location_name: string;
  quantity_on_hand: number;
  quantity_foh: number;
  quantity_boh: number;
  in_transit: number;
  on_order: number;
  reorder_point: number;
  desired_level: number;
  need_to_order: number;
  last_cost: number;
  is_negative: boolean;
}

export interface ReorderListFilters {
  locationId?: string;
  vendorId?: string;
  categoryId?: string;
  comparisonLocationId?: string;
}

export function useReorderList(
  filters: ReorderListFilters = {},
  options: { enabled?: boolean } = {}
) {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  // Fetch reorder list
  const {
    data: reorderItems,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['reorder-list', organizationId, filters],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_reorder_list', {
        p_organization_id: organizationId,
        p_location_id: filters.locationId || null,
        p_vendor_id: filters.vendorId || null,
        p_category_id: filters.categoryId || null,
      });

      if (error) throw error;
      return (data || []) as ReorderItem[];
    },
    enabled: !!organizationId && (options.enabled ?? true),
  });

  // Fetch comparison location stock if specified
  const { data: comparisonStock } = useQuery({
    queryKey: ['comparison-stock', organizationId, filters.comparisonLocationId],
    queryFn: async () => {
      if (!organizationId || !filters.comparisonLocationId) return {};

      const { data, error } = await supabase
        .from('inventory_items')
        .select('product_id, quantity_on_hand, quantity_foh, quantity_boh')
        .eq('location_id', filters.comparisonLocationId);

      if (error) throw error;

      // Convert to lookup map
      const stockMap: Record<string, { qoh: number; foh: number; boh: number }> = {};
      (data || []).forEach((item) => {
        stockMap[item.product_id] = {
          qoh: item.quantity_on_hand || 0,
          foh: item.quantity_foh || item.quantity_on_hand || 0,
          boh: item.quantity_boh || 0,
        };
      });
      return stockMap;
    },
    enabled: !!organizationId && !!filters.comparisonLocationId && (options.enabled ?? true),
  });

  // Combine reorder items with comparison stock
  const itemsWithComparison = (reorderItems || []).map((item) => ({
    ...item,
    comparison_qoh: comparisonStock?.[item.product_id]?.qoh || 0,
    comparison_foh: comparisonStock?.[item.product_id]?.foh || 0,
    comparison_boh: comparisonStock?.[item.product_id]?.boh || 0,
  }));

  // Calculate summary stats
  const summary = {
    totalItems: itemsWithComparison.length,
    totalNeed: itemsWithComparison.reduce((sum, item) => sum + item.need_to_order, 0),
    totalValue: itemsWithComparison.reduce((sum, item) => sum + (item.need_to_order * item.last_cost), 0),
    negativeItems: itemsWithComparison.filter((item) => item.is_negative).length,
    outOfStock: itemsWithComparison.filter((item) => item.quantity_on_hand <= 0).length,
  };

  // Group by vendor for PO creation
  const groupedByVendor = itemsWithComparison.reduce((acc, item) => {
    const vendorId = item.vendor_id || 'no_vendor';
    const vendorName = item.vendor_name || 'No Supplier';
    if (!acc[vendorId]) {
      acc[vendorId] = {
        vendorId,
        vendorName,
        items: [],
        totalNeed: 0,
        totalValue: 0,
      };
    }
    acc[vendorId].items.push(item);
    acc[vendorId].totalNeed += item.need_to_order;
    acc[vendorId].totalValue += item.need_to_order * item.last_cost;
    return acc;
  }, {} as Record<string, { vendorId: string; vendorName: string; items: ReorderItem[]; totalNeed: number; totalValue: number }>);

  return {
    reorderItems: itemsWithComparison,
    isLoading,
    error,
    refetch,
    summary,
    groupedByVendor: Object.values(groupedByVendor),
  };
}
